/**
 * CustomHeader Component
 *
 * A unified custom header component for AG Grid that supports multiple modes:
 * - compsheet: Full sorting, filtering, graph navigation features
 * - financial: Simple display, no sorting, with alignment support
 * - simple: Basic header without interactive features
 *
 * Props:
 *   mode: 'compsheet' | 'financial' | 'simple' (default: auto-detect based on other props)
 *   alignment: 'left' | 'right' | 'center' (default: 'left')
 *   displayLogo: boolean - Show SoftwareIQ logo
 *   sublabel: string - Secondary text below header
 *   enableFilterButton: boolean - Show filter menu button
 *   displayGraph: boolean - Show graph navigation button
 *   displayScatter: boolean - Navigate to scatter plot instead of bar chart
 *   isSelected: boolean - Whether this column is selected (highlights header)
 *   selectedColor: string - Color to use when selected
 */

import React, { useEffect, useRef, useState } from "react";
import { IconButton, Tooltip, Box } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTheme } from "@mui/material/styles";
import BarChartIcon from '@mui/icons-material/BarChart';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import { useNavigate } from "react-router-dom";
import { setDashboardMetricSelection, setGlobalActiveMetric } from "../../utils";

// Header modes
export const HEADER_MODES = {
  COMPSHEET: 'compsheet',
  FINANCIAL: 'financial',
  SIMPLE: 'simple',
};

const CustomHeader = (props) => {
  const [sortState, setSortState] = useState(null);
  const refButton = useRef(null);
  const navigate = useNavigate();
  const theme = useTheme();

  // Determine mode from props
  const mode = props.mode || (
    props.alignment === 'Right' || props.alignment === 'right'
      ? HEADER_MODES.FINANCIAL
      : HEADER_MODES.COMPSHEET
  );

  // Alignment support - default to center, leftmost columns should explicitly set 'left'
  const alignment = props.alignment || 'center';
  const alignRight = alignment === 'Right' || alignment === 'right';
  const alignCenter = alignment === 'center';
  const alignLeft = alignment === 'Left' || alignment === 'left';

  // Feature flags based on mode
  const enableSorting = mode === HEADER_MODES.COMPSHEET && props.column?.isSortable?.();
  const displayHeader = props.displayLogo;
  const displayGraph = props.displayGraph && mode === HEADER_MODES.COMPSHEET;
  const displayScatter = props.displayScatter;

  // Update sorting state when it changes
  const onSortChanged = () => {
    if (props.column?.getSort) {
      setSortState(props.column.getSort());
    }
  };

  // Handle sorting when clicking the header (except filter button)
  const onHeaderClicked = (event) => {
    if (!enableSorting) return;
    if (refButton.current && refButton.current.contains(event.target)) return;
    const nextSort = sortState === "desc" ? "asc" : sortState === "asc" ? null : "desc";
    props.setSort?.(nextSort, event.shiftKey);
  };

  // Show column menu
  const onMenuClicked = () => {
    props.showColumnMenu?.(refButton.current);
  };

  // Graph navigation - switches view and sets selected metric
  const onGraphClicked = (event) => {
    // The graph action lives inside a sortable header, so keep its click from
    // reaching the header's sort handler.
    event.stopPropagation();

    const field = props.column?.colDef?.field;
    if (field) {
      if (displayScatter) {
        setGlobalActiveMetric(field);
      } else {
        setDashboardMetricSelection('industryMetrics', [field]);
      }
      // Compatibility for legacy callers while dashboard storage migrates.
      localStorage.setItem("selectedfields", JSON.stringify([field]));
    }

    // Set the target view and dispatch event to trigger view change
    const targetView = displayScatter ? 'scatter' : 'chart';
    localStorage.setItem('industryMetricsView', targetView);

    // Dispatch custom event to notify UnifiedMetrics of view change
    window.dispatchEvent(new CustomEvent('metricsViewChange', {
      detail: { view: targetView }
    }));

    navigate(`/kpis/vendor/${targetView}`);
  };

  useEffect(() => {
    if (enableSorting && props.column) {
      props.column.addEventListener("sortChanged", onSortChanged);
      onSortChanged();
      return () => props.column.removeEventListener("sortChanged", onSortChanged);
    }
  }, [enableSorting, props.column]);

  // Determine content alignment style
  const getAlignmentStyle = () => {
    if (alignRight) {
      return {
        alignItems: "flex-end",
        textAlign: "right",
      };
    }
    if (alignCenter) {
      return {
        alignItems: "center",
        textAlign: "center",
      };
    }
    return {
      alignItems: "flex-start",
      textAlign: "left",
    };
  };

  const alignmentStyle = getAlignmentStyle();

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        cursor: enableSorting ? "pointer" : "default",
        userSelect: "none",
      }}
      onClick={onHeaderClicked}
    >
      {/* Display logo if applicable */}
      {displayHeader && (
        <img
          src="/logosmall.png"
          alt="logo"
          className="icon"
          style={{ maxWidth: "35px", maxHeight: "35px", paddingRight: "4px", opacity: 0.4 }}
        />
      )}

      {/* Column Title with Sublabel */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          ...alignmentStyle,
        }}
      >
        <span style={{
          fontWeight: props.isSelected ? 600 : "bold",
          color: props.isSelected ? (props.selectedColor || theme.palette.primary.main) : 'inherit',
        }}>
          {props.displayName}
        </span>
        {props.sublabel && (
          <span style={{ fontSize: "smaller", color: "gray", marginTop: mode === HEADER_MODES.FINANCIAL ? "2px" : "-2px" }}>
            {props.sublabel}
          </span>
        )}
      </div>

      {/* Icons (Right-Aligned) - Only for compsheet mode */}
      {mode === HEADER_MODES.COMPSHEET && (
        <Box sx={{ color: theme.palette.text.secondary, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Filter Button */}
          {props.enableFilterButton && (
            <IconButton
              sx={{
                color: theme.palette.text.secondary,
                marginBottom: displayGraph
                  ? (sortState ? "-6px" : "0px")
                  : (sortState ? "0px" : "14px"),
              }}
              size="small"
              ref={refButton}
              onClick={onMenuClicked}
            >
              <FilterListIcon sx={{ fontSize: '14px' }} />
            </IconButton>
          )}

          {/* Graph Button */}
          {displayGraph && (
            <Tooltip title={displayScatter ? "View in Valuation" : "View in Metrics"}>
              <IconButton
                sx={{ padding: "4px", color: theme.palette.text.secondary }}
                size="small"
                aria-label={`View ${props.displayName} in ${displayScatter ? "Valuation" : "Metrics"}`}
                onClick={onGraphClicked}
              >
                {displayScatter ? (
                  <ScatterPlotIcon sx={{ fontSize: '13px' }} />
                ) : (
                  <BarChartIcon sx={{ fontSize: '13px' }} />
                )}
              </IconButton>
            </Tooltip>
          )}

          {/* Sort Icon */}
          {sortState && (
            <Tooltip title={sortState === "asc" ? "Sorted Ascending" : "Sorted Descending"}>
              {sortState === "asc" ? (
                <ArrowUpwardIcon sx={{ fontSize: "12px" }} />
              ) : (
                <ArrowDownwardIcon sx={{ fontSize: "12px" }} />
              )}
            </Tooltip>
          )}
        </Box>
      )}
    </div>
  );
};

export default CustomHeader;
